/**
 * Decides which national stations are worth refreshing from the ten-minute feed.
 *
 * The map draws these from NDBC's bulk file, which is an hourly rollup and lags
 * `realtime2` by up to an hour for some stations. That was tolerable while the
 * marker was the only thing on screen, but opening one fetches `realtime2` and
 * shows a different, fresher number — the map and the panel disagreeing about
 * the same buoy at the same moment.
 *
 * Refreshing every visible station would be one upstream fetch each, which is
 * the cost the bulk file exists to avoid. So this runs only when few enough are
 * on screen to be read individually, which is exactly when a stale number is
 * worth correcting, and it remembers what it has already asked for.
 */

/** Above this many markers, the view is a map rather than a set of readings. */
export const LIVE_REFRESH_MAX = 25;

/**
 * How long a station stays refreshed before it is worth asking again.
 *
 * Must stay below the app's refresh poll, currently six minutes. Nothing else
 * drives this when the reader sits still: the poll repaints, the repaint plans
 * a refresh, and the plan only asks for stations that have gone stale. Raise
 * this above the poll interval and the national markers quietly stop updating
 * on their own — which is the bug this whole mechanism exists to fix.
 */
export const LIVE_REFRESH_TTL_MS = 5 * 60_000;

export interface RefreshPlan {
  /** Stations to ask for, empty when there is nothing worth doing. */
  ids: string[];
}

/**
 * @param visible  Stations currently drawn.
 * @param lastSeen When each station was last refreshed.
 * @param now      Injectable for tests.
 */
export function planRefresh(
  visible: readonly string[],
  lastSeen: ReadonlyMap<string, number>,
  now = Date.now(),
  max = LIVE_REFRESH_MAX,
): RefreshPlan {
  // Zoomed out far enough that individual numbers cannot be read anyway, and
  // refreshing them would be dozens of upstream fetches for nothing.
  if (visible.length === 0 || visible.length > max) return { ids: [] };

  const ids = visible.filter((id) => {
    const at = lastSeen.get(id);
    return at === undefined || now - at >= LIVE_REFRESH_TTL_MS;
  });

  return { ids };
}

/** The fields a refresh may replace. Structural, so callers keep their own types. */
export interface Refreshable {
  id: string;
  t: Date;
  speedKt: number | null;
  dirDeg: number | null;
  gustKt: number | null;
  waveHeightM: number | null;
  airTempC: number | null;
  waterTempC: number | null;
}

/**
 * Folds fresher readings into the stations already on the map.
 *
 * Strictly newer wins, and nothing else is touched. The bulk file and
 * `realtime2` are the same measurements arriving by different routes, so a row
 * that is not newer has nothing to add — and overwriting on equal timestamps
 * would repaint on every pass, which the caller uses to decide whether to
 * redraw at all.
 *
 * @returns whether anything actually changed.
 */
export function applyFresher(
  stations: readonly Refreshable[],
  rows: ReadonlyMap<string, Omit<Refreshable, 'id'>>,
): boolean {
  let changed = false;

  for (const s of stations) {
    const r = rows.get(s.id);
    if (!r || r.t <= s.t) continue;

    s.t = r.t;
    s.speedKt = r.speedKt;
    s.dirDeg = r.dirDeg;
    s.gustKt = r.gustKt;
    s.waveHeightM = r.waveHeightM;
    s.airTempC = r.airTempC;
    s.waterTempC = r.waterTempC;
    changed = true;
  }

  return changed;
}

/**
 * Whether the bulk national file is worth re-reading.
 *
 * Asking more often than the edge refreshes it would spend bandwidth on bytes
 * that cannot have changed, so the floor is the edge's own lifetime. This is
 * what keeps a crowded view — too many markers for the per-station refresh —
 * from sitting on its opening readings for the life of the tab.
 */
export function shouldReloadBulk(
  lastAt: number | null,
  now: number,
  minIntervalMs: number,
): boolean {
  if (lastAt === null) return false;   // nothing loaded yet; the initial load owns that
  return now - lastAt >= minIntervalMs;
}
